import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { useState, useCallback } from 'react';
import { useReactNodeView } from '../utils/ReactNodeView';
import styled from 'styled-components';
import { getTokenTooltip } from '../utils/personalizationTokenUtils';
import { PersonalizationTokenPopover } from '../commands/InsertPersonalizationToken';
import { CompaniesIcon } from 'foundations-assets/icons/components/CompaniesIcon';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { MeetingsIcon } from 'foundations-assets/icons/components/MeetingsIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const personalizationTokenIconMap = {
  companies: CompaniesIcon,
  contacts: ContactsIcon,
  meetings: MeetingsIcon
};
export const StyledToken = styled.span.withConfig({
  displayName: "PersonalizationTokenView__StyledToken"
})(["align-items:center;background-color:", ";border:", " solid ", ";border-radius:", ";color:", ";cursor:default;display:inline-flex;line-height:1;margin:", ";padding:", ";vertical-align:middle;user-select:text;white-space:nowrap;inline-size:auto;"], ({
  theme,
  backgroundColor
}) => backgroundColor || theme.color['fill-info-subtle'], ({
  theme
}) => theme.borderWidth['100'], ({
  theme,
  borderColor
}) => borderColor || theme.color['border-core-default'], ({
  theme
}) => theme.borderRadius['100'], ({
  theme,
  fontColor
}) => fontColor || theme.color['fill-info-default'], ({
  theme,
  margin
}) => margin || theme.space['0'], ({
  theme,
  padding
}) => padding || `${theme.space['100']} ${theme.space['200']}`);
const getRenderedDisplayText = ({
  displayText,
  tokenKey,
  defaultTokenValue
}) => {
  const baseText = displayText || tokenKey;
  return defaultTokenValue ? `${baseText} (${defaultTokenValue})` : baseText;
};
export function PersonalizationTokenView() {
  var _node$attrs;
  const {
    node,
    editorConfig,
    view,
    getPos
  } = useReactNodeView();
  const [open, setOpen] = useState(false);
  const {
    tokenKey,
    defaultTokenValue,
    tokenStyles,
    tokenMeta,
    displayText
  } = (_node$attrs = node === null || node === void 0 ? void 0 : node.attrs) !== null && _node$attrs !== void 0 ? _node$attrs : {};
  const tokenIconKey = tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.tokenIconKey;
  const tooltipConfig = getTokenTooltip(editorConfig, tokenKey);
  const onOpenChange = useCallback(e => {
    if (e.target) {
      setOpen(e.target.value);
    }
  }, [setOpen]);
  const handleClick = useCallback(() => {
    if (!(view !== null && view !== void 0 && view.editable)) return;
    setOpen(prevState => !prevState);
  }, [setOpen, view]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const personalizationTokenPopover = useCallback(() => /*#__PURE__*/_jsx(PersonalizationTokenPopover, {
    initialTokenKey: tokenKey,
    initialDefaultValue: defaultTokenValue,
    initialTokenMeta: tokenMeta,
    nodePos: typeof getPos === 'function' ? getPos() : undefined,
    onPopoverConfirm: handleClose
  }), [tokenKey, defaultTokenValue, tokenMeta, getPos, handleClose]);
  const tokenContent =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsxs(StyledToken, {
    backgroundColor: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.backgroundColor,
    borderColor: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.borderColor,
    fontColor: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.fontColor,
    margin: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.margin,
    padding: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.padding,
    "data-test-id": "ProseMirror__personalizationToken",
    onClick: handleClick,
    style: {
      cursor: view !== null && view !== void 0 && view.editable ? 'pointer' : 'default'
    },
    children: [personalizationTokenIconMap[tokenIconKey] && /*#__PURE__*/_jsx(IconHolder, {
      icon: personalizationTokenIconMap[tokenIconKey],
      marginInlineEnd: "200",
      padding: "0"
    }), (tooltipConfig === null || tooltipConfig === void 0 ? void 0 : tooltipConfig.icon) && /*#__PURE__*/_jsx(IconHolder, {
      icon: tooltipConfig.icon,
      marginInlineEnd: "200",
      padding: "0"
    }), getRenderedDisplayText({
      displayText,
      tokenKey,
      defaultTokenValue
    })]
  });
  const wrappedTokenContent = tooltipConfig ? /*#__PURE__*/_jsx(Tooltip, {
    title: tooltipConfig.title,
    placement: "top",
    children: tokenContent
  }) : tokenContent;
  return /*#__PURE__*/_jsx(Popover, {
    open: open,
    onOpenChange: onOpenChange,
    Content: personalizationTokenPopover,
    children: /*#__PURE__*/_jsx("span", {
      "data-test-id": "ProseMirror__personalizationToken__wrapper",
      children: wrappedTokenContent
    })
  });
}
PersonalizationTokenView.displayName = 'PersonalizationTokenView';
export function personalizationTokenViewUpdateFn({
  previousNode,
  node
}) {
  if (previousNode.attrs.tokenKey !== node.attrs.tokenKey || previousNode.attrs.defaultTokenValue !== node.attrs.defaultTokenValue || previousNode.attrs.displayText !== node.attrs.displayText || JSON.stringify(previousNode.attrs.tokenMeta) !== JSON.stringify(node.attrs.tokenMeta)) {
    return false;
  }
  return true;
}